import type { ByValueFilter } from '@openx/components-table/serverSideCriteriaProcessing';
import { parseMultiValueFilter } from '@openx/utils/lib/multiValueFilter';

type RawPagination = { pageNumber?: number; pageSize?: number };
type RawSort = { column?: string; direction?: string };

type DealLandingCriteria = {
  pagination?: RawPagination;
  sort?: RawSort;
  filters?: {
    phrase?: string;
    byPhrase?: { fields?: string[]; phrase?: string };
    byValue?: ByValueFilter;
    [key: string]: unknown;
  };
};

/**
 * Fields where a single CSV string value should be split into multiple values.
 * Mirrors `dealListMultiValueFilters` in serverSideCriteriaProcessing.
 */
const multiValueFields = new Set([
  'account',
  'createdBy',
  'status',
  'deal_id',
  'exchange_deal_id',
  'package_uid',
  'pmp_deal_type',
  'demand_partner',
  'buyer_ids',
]);

/**
 * Serialize deal list criteria (from DealsList) to a queryString compatible with the
 * `/deal/landing` REST endpoint, using the same parameter conventions as the legacy `/deal` endpoint.
 */
export function serializeDealLandingQueryString(criteria: DealLandingCriteria): string {
  const params = new URLSearchParams();

  // Pagination
  const pageSize = criteria.pagination?.pageSize ?? 25;
  const pageNumber = criteria.pagination?.pageNumber ?? 1;
  params.set('limit', String(pageSize));
  const offset = (pageNumber - 1) * pageSize;
  if (offset > 0) {
    params.set('offset', String(offset));
  }

  // Sort
  if (criteria.sort?.column && criteria.sort?.direction) {
    const prefix = criteria.sort.direction === 'desc' ? '-' : '';
    params.set('sort', `${prefix}${criteria.sort.column}`);
  }

  const filters = criteria.filters;

  // Phrase search → `q`
  const phrase = filters?.byPhrase?.phrase ?? filters?.phrase ?? '';
  if (phrase.trim()) {
    params.set('q', phrase.trim());
  }

  // Value-based filters (from byValue array)
  if (filters?.byValue) {
    for (const { field, values } of filters.byValue) {
      if (!values || values.length === 0) continue;

      const paramName = mapDealFilterField(field);
      const normalizedValues = normalizeValues(field, values);

      if (field === 'demand_partner' || field === 'buyer_ids') {
        // DSP uses CSV param (no __in) matching legacy /deal REST pattern
        params.set(paramName, normalizedValues.join(','));
      } else if (field === 'account') {
        // /deal/landing multi-account filtering works with MGMT account_uid__in, not account_id__in.
        // Account filter values are normalized to MGMT account UIDs before serialization.
        if (normalizedValues.length > 1) {
          params.set(`${paramName}__in`, normalizedValues.join(','));
        } else {
          params.set(paramName, String(normalizedValues[0]));
        }
      } else if (normalizedValues.length > 1) {
        params.set(`${paramName}__in`, normalizedValues.join(','));
      } else {
        params.set(paramName, String(normalizedValues[0]));
      }
    }
  }

  // Metric range filters (min/max pairs) — mapped from flat filter keys to __gte/__lte params
  if (filters) {
    const metricRangeFilters: Array<[string, string]> = [
      ['spend_min', 'spend__gte'],
      ['spend_max', 'spend__lte'],
      ['impressions_min', 'impressions__gte'],
      ['impressions_max', 'impressions__lte'],
      ['cpm_min', 'cpm__gte'],
      ['cpm_max', 'cpm__lte'],
      ['publisher_revenue_min', 'publisher_revenue__gte'],
      ['publisher_revenue_max', 'publisher_revenue__lte'],
      ['publisher_cpm_min', 'publisher_cpm__gte'],
      ['publisher_cpm_max', 'publisher_cpm__lte'],
      ['partner_revenue_min', 'partner_revenue__gte'],
      ['partner_revenue_max', 'partner_revenue__lte'],
      ['health_score_min', 'health_score__gte'],
      ['health_score_max', 'health_score__lte'],
    ];

    for (const [filterKey, paramName] of metricRangeFilters) {
      const value = filters[filterKey];
      if (value != null && value !== '') {
        params.set(paramName, String(value));
      }
    }
  }

  return params.toString();
}

/**
 * Normalize filter values: split multi-value CSV strings for deal list fields,
 * matching the behavior in serverSideCriteriaProcessing.
 */
function normalizeValues(field: string, values: (string | number | null | undefined)[]): string[] {
  if (!multiValueFields.has(field)) {
    return values.filter(v => v != null).map(String);
  }

  if (values.length !== 1 || typeof values[0] !== 'string') {
    return values.filter(v => v != null).map(String);
  }

  return parseMultiValueFilter(values[0])
    .filter(v => v != null)
    .map(String);
}

/**
 * Map hasura-style frontend filter field names to the REST param names
 * that the legacy `/deal` and landing endpoints expect.
 */
function mapDealFilterField(field: string): string {
  switch (field) {
    case 'exchange_deal_id':
      return 'deal_id';
    case 'createdBy':
      return 'created_by_user_id';
    case 'status':
      return 'status';
    case 'account':
      return 'account_uid';
    default:
      return field;
  }
}
